import { makeStyles } from '@map/theme';
import { AppBar, AppBarProps, Toolbar } from '@mui/material';
import { SUBCONTENT_MAX_WIDTH } from '@shared/theme';
import NavButton from '@ui/NavButton';
import { ReactNode, forwardRef, useMemo } from 'react';
import HelpButton from '../HelpButton';

// this matches the width of the value proposition cards
const LARGE_DESKTOP_WIDTH = 278; // in sizing units

const useStyles = makeStyles(({ spacing, breakpoints, zIndex, sizing }) => ({
  root: {
    [breakpoints.up('md')]: {
      zIndex: zIndex.appBar,
    },
  },
  toolbar: {
    justifyContent: 'space-between',
    boxSizing: 'content-box',
    padding: spacing(2.5, 4),
    display: 'flex',
    '& > :first-child:last-child': {
      marginLeft: 'auto',
    },
    [breakpoints.up('md')]: {
      height: '100%',
      width: sizing(SUBCONTENT_MAX_WIDTH),
      margin: '0 auto',
    },
    [breakpoints.up('lg')]: {
      width: sizing(LARGE_DESKTOP_WIDTH),
    },
  },
  childrenWrapper: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

type Props = {
  className?: string;
  inverse?: boolean;
  backLink?: (() => void) | string | true;
  position?: AppBarProps['position'];
  children?: ReactNode;
};

const GuestAreaAppBar: FFC<Props> = (props, ref) => {
  const { backLink, children, className, position, inverse = false } = props;
  const { classes, cx } = useStyles();
  const backButton = useMemo(() => {
    if (!backLink) {
      return null;
    }
    const which = 'appbar-back';
    if (typeof backLink === 'string') {
      return <NavButton which={which} to={backLink} />;
    }
    if (backLink === true) {
      return <NavButton which={which} to={-1} inverse={inverse} />;
    }
    return <NavButton which={which} onClick={backLink} inverse={inverse} />;
  }, [backLink, inverse]);

  return (
    <AppBar
      ref={ref}
      color="transparent"
      position={position || 'static'}
      className={cx(classes.root, className)}
    >
      <Toolbar variant="dense" className={classes.toolbar}>
        {backButton}
        <div className={classes.childrenWrapper}>
          {children}
          <HelpButton inverse={inverse} hideWhenInactive={true} />
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default forwardRef(GuestAreaAppBar);
